import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, Building2, MapPin, ArrowRight, Loader2, CheckCircle, XCircle, Info, Pencil } from 'lucide-react';

const abnChecksumValid = (digits) => {
  const w = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const d = digits.split('').map(Number);
  d[0] -= 1;
  return d.reduce((s, n, i) => s + n * w[i], 0) % 89 === 0;
};

function AbnHint({ query, resultsCount, searching }) {
  if (!/^[\d\s]+$/.test(query || '')) return null;
  const digits = (query || '').replace(/\D/g, '');
  if (digits.length < 6) return null;
  let icon = Info, color = '#5C6B7A', text = '';
  if (digits.length < 11) {
    text = `Partial number (${digits.length} digits) — ABNs are 11 digits, ACNs are 9, NZBNs are 13`;
  } else if (digits.length === 11) {
    if (abnChecksumValid(digits)) { icon = CheckCircle; color = '#1D9E75'; text = 'Valid ABN format — searching the register (spaces are fine)'; }
    else if (searching) { text = 'Checking the register…'; }
    else if (resultsCount > 0) { icon = CheckCircle; color = '#1D9E75'; text = 'ABN matched in the entity register'; }
    else { icon = XCircle; color = '#E24B4A'; text = 'Invalid ABN — fails the ATO checksum and no register match. Please re-check the number.'; }
  } else if (digits.length === 13) {
    text = '13 digits — searching as an NZBN';
  } else {
    icon = XCircle; color = '#E24B4A'; text = `${digits.length} digits — ABNs are 11 digits (NZBNs are 13)`;
  }
  const Icon = icon;
  return (
    <p className="flex items-center gap-1.5 text-[11px] mt-2" style={{ color }} data-testid="abn-validation-hint">
      <Icon className="w-3 h-3" />{text}
    </p>
  );
}

export default function SearchBar({ query, onSearch, searching, results, onSelect, onEdit }) {
  return (
    <>
      <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#5C6B7A' }} />
          <Input data-testid="broker-search-input" value={query} onChange={onSearch}
            placeholder="Search by ABN, NZBN, or business name..."
            className="pl-10 rounded-sm border-[rgba(136,150,168,0.3)] focus:ring-2 focus:ring-[#1A4C7C]" />
          {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" style={{ color: '#00B4D8' }} />}
        </div>
        <AbnHint query={query} resultsCount={results.length} searching={searching} />
      </Card>

      {results.length > 0 && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white divide-y divide-[rgba(136,150,168,0.1)]">
          {results.map((entity) => (
            <button key={entity.entity_id} data-testid={`entity-result-${entity.entity_id}`}
              onClick={() => onSelect(entity)}
              className="w-full p-4 flex items-center justify-between hover:bg-[#F5F7FA] transition-colors text-left">
              <div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" style={{ color: '#1A4C7C' }} />
                  <span className="font-medium text-sm" style={{ color: '#0D1B2A' }}>{entity.legal_name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-medium"
                    style={{
                      background: entity.status === 'Active' ? 'rgba(29,158,117,0.1)' : 'rgba(226,75,74,0.1)',
                      color: entity.status === 'Active' ? '#1D9E75' : '#E24B4A'
                    }}>
                    {entity.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs" style={{ color: '#5C6B7A' }}>
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{entity.city}, {entity.state}</span>
                  <span>{entity.abn ? `ABN: ${entity.abn}` : `NZBN: ${entity.nzbn}`}</span>
                  <span>{entity.industry_code}</span>
                </div>
              </div>
              <span className="flex items-center gap-2 shrink-0">
                {onEdit && (
                  <span role="button" tabIndex={0} data-testid={`entity-edit-${entity.entity_id}`}
                    onClick={(e) => { e.stopPropagation(); onEdit(entity); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onEdit(entity); } }}
                    title="Edit business details"
                    className="p-1.5 rounded-sm transition-colors hover:bg-[rgba(26,76,124,0.1)]">
                    <Pencil className="w-3.5 h-3.5" style={{ color: '#1A4C7C' }} />
                  </span>
                )}
                <ArrowRight className="w-4 h-4" style={{ color: '#5C6B7A' }} />
              </span>
            </button>
          ))}
        </Card>
      )}
    </>
  );
}
